import { Link } from '@tanstack/react-router';
import { Camera, ChevronRight } from 'lucide-react';
import { cn } from '@jeraisonne/ui';
import { AvatarCircle } from '@/components/avatar-circle';
import { useSubmissionPhotoUrl } from '@/lib/exercise-submissions/api';
import { formatRelativeDate } from './format';
import type { SubmissionFeedRow, ViewerRole } from './types';

const STATUS_META: Record<SubmissionFeedRow['status'], { label: string; cls: string }> = {
  pending: { label: 'En attente', cls: 'bg-amber-100 text-amber-700' },
  used: { label: 'Utilisé', cls: 'bg-green-100 text-green-700' },
  dismissed: { label: 'Ignoré', cls: 'bg-ink-100 text-ink-500' },
};

/** Libellé d'en-tête selon qui regarde (prof destinataire / élève expéditeur). */
export function submissionHeadline(
  viewerRole: ViewerRole,
  data: Pick<SubmissionFeedRow, 'student' | 'tutor'>,
): { name: string; avatar: string | null; line: string } {
  if (viewerRole === 'tutor') {
    return {
      name: data.student?.display_name ?? 'Un élève',
      avatar: data.student?.avatar_url ?? null,
      line: ' t’a envoyé un exercice',
    };
  }
  return {
    name: 'Tu',
    avatar: data.student?.avatar_url ?? null,
    line: ` as envoyé un exercice à ${data.tutor?.display_name ?? 'ton prof'}`,
  };
}

/**
 * Carte « X a envoyé un exercice à son prof » (migrations 0222-0223). Côté prof,
 * toute la carte ouvre la boîte « Exercices reçus » ; côté élève, simple rappel.
 */
export function SubmissionCard({
  data,
  viewerRole,
}: {
  data: SubmissionFeedRow;
  viewerRole: ViewerRole;
}) {
  const { name, avatar, line } = submissionHeadline(viewerRole, data);
  const firstPhoto = [...(data.exercise_submission_photos ?? [])].sort(
    (a, b) => a.position - b.position,
  )[0];
  const photoCount = data.exercise_submission_photos?.length ?? 0;
  const { data: photoUrl } = useSubmissionPhotoUrl(firstPhoto?.storage_path);
  const status = STATUS_META[data.status] ?? STATUS_META.pending;
  const clickable = viewerRole === 'tutor';

  const body = (
    <div className="flex gap-3 p-4">
      <div className="min-w-0 flex-1 space-y-1.5">
        <div className="flex flex-wrap items-center gap-1.5">
          <Camera className="text-primary-400 h-3.5 w-3.5 shrink-0" />
          <span className="text-primary-600 font-sans text-[11px] font-semibold uppercase tracking-wider">
            Exercice envoyé
          </span>
          <span
            className={cn(
              'ml-auto shrink-0 rounded-full px-2.5 py-0.5 font-sans text-[11px] font-semibold',
              status.cls,
            )}
          >
            {status.label}
          </span>
        </div>
        <p className="text-ink-900 font-sans text-sm font-semibold leading-snug">
          {data.chapters?.title ?? 'Exercice en photo'}
        </p>
        {data.note.trim() && (
          <p className="text-ink-600 line-clamp-2 font-sans text-xs italic">« {data.note} »</p>
        )}
        <p className="text-ink-500 font-sans text-xs">
          {photoCount} photo{photoCount > 1 ? 's' : ''}
        </p>
      </div>
      {photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          loading="lazy"
          aria-hidden="true"
          className="bg-ink-50 h-20 w-20 shrink-0 rounded-xl object-cover sm:h-24 sm:w-24"
        />
      ) : (
        <div
          aria-hidden="true"
          className="from-primary-100 to-primary-300 bg-linear-to-br flex h-20 w-20 shrink-0 items-center justify-center rounded-xl sm:h-24 sm:w-24"
        >
          <Camera className="text-primary-500 h-6 w-6" />
        </div>
      )}
      {clickable && (
        <ChevronRight className="text-ink-400 h-4 w-4 shrink-0 self-center" aria-hidden="true" />
      )}
    </div>
  );

  return (
    <div className="overflow-hidden rounded-2xl bg-white">
      <div className="flex items-center gap-2.5 px-4 py-3">
        <AvatarCircle name={viewerRole === 'tutor' ? name : 'Moi'} src={avatar} size="sm" />
        <div className="min-w-0 flex-1">
          <span className="text-ink-900 font-sans text-xs font-semibold">{name}</span>
          <span className="text-ink-500 font-sans text-xs">{line}</span>
        </div>
        <span className="text-ink-400 shrink-0 font-sans text-[11px]">
          {formatRelativeDate(data.created_at)}
        </span>
      </div>
      {clickable ? (
        <Link
          to="/tutor/submissions"
          aria-label={`Ouvrir l’exercice envoyé par ${name}`}
          className="block transition-transform hover:bg-white/5 active:scale-[0.98]"
        >
          {body}
        </Link>
      ) : (
        body
      )}
    </div>
  );
}
